import React, {
  useState,
  useMemo,
  Suspense,
  useCallback,
  useEffect
} from "react";
import { debounce } from "lodash";
import themePropTypes from "../theme-prop-types";
import { INPUT_UPDATE_TIMEOUT, PHONE_FIELD } from "../../utils/constants";

const PhoneInput = React.lazy(() => import("react-phone-number-input"));
const Input = React.lazy(() => import("react-phone-number-input/input"));
const CountryIcon = React.lazy(() =>
  import("react-phone-number-input/modules/CountryIcon")
);

export const getPhoneNumberWithCountryCode = ({ countryCode, phoneNumber }) =>
  phoneNumber && phoneNumber[0] !== "+"
    ? `+${countryCode}${phoneNumber}`
    : phoneNumber;

export const getPhoneFieldValue = value => {
  if (typeof value === "string" && value.includes(PHONE_FIELD.phoneNumber)) {
    const extracted = JSON.parse(value);
    return {
      ...extracted,
      phoneNumber: getPhoneNumberWithCountryCode(extracted)
    };
  } else if (value?.phoneNumber) {
    return {
      ...value,
      phoneNumber: getPhoneNumberWithCountryCode(value)
    };
  }
};

const PhonePicker = ({
  components: { Label, Select },
  disabled = false,
  name,
  title,
  params = "",
  values,
  options,
  onUpdate,
  isRequired,
  isInvalid,
  customCountrySelect = true
}) => {
  const defaultCountry = useMemo(
    () => (options.length ? options[0].name : null),
    [options]
  );

  const [countryName, setCountryName] = useState(
    getPhoneFieldValue(values[name])?.countryName || defaultCountry
  );
  const [phoneNumber, setPhoneNumber] = useState(
    getPhoneFieldValue(values[name])?.phoneNumber
  );

  const className = useMemo(
    () =>
      name +
      params?.split("|").join(" ") +
      (isInvalid ? " invalid" : "") +
      (customCountrySelect ? " custom-dropdown" : ""),
    [name, params, isInvalid]
  );
  const disableCountrySelect = useMemo(
    () => params.split("|").includes("TDFieldOptionDisabledCountryPicker"),
    [params]
  );
  const countryOptions = useMemo(
    () => options.map(option => option.name).filter(option => option),
    [options]
  );
  const labels = useMemo(
    () => options.reduce((acc, val) => ({ ...acc, [val.name]: val.text }), {}),
    [options]
  );
  const countriesOrder = useMemo(
    () => options.map(country => country.name),
    [options]
  );
  const countryCode = useMemo(
    () => options.find(opt => opt.name === countryName)?.value,
    [countryName, options]
  );
  const country = useMemo(
    () => getPhoneFieldValue(values[name])?.countryName || countryName,
    [countryName]
  );

  const updateNumberHandler = useCallback(newValue => {
    if (newValue) {
      setPhoneNumber(newValue);
      onUpdate(name, {
        countryName,
        countryCode,
        phoneNumber: getPhoneFieldValue({ countryCode, phoneNumber: newValue })
          ?.phoneNumber
      });
    }
  });

  const debouncedChangeHandler = useMemo(
    () => debounce(updateNumberHandler, INPUT_UPDATE_TIMEOUT),
    []
  );

  useEffect(() => () => debouncedChangeHandler.cancel(), []);

  const onBlurNumberInput = () =>
    onUpdate(name, {
      countryName,
      countryCode,
      phoneNumber: getPhoneFieldValue({ countryCode, phoneNumber })?.phoneNumber
    });

  const customOptions = useMemo(
    () => options.map(option => ({ value: option.name, label: option.text })),
    [options]
  );

  const onCustomCountryChange = useCallback(code => {
    setPhoneNumber("");
    setCountryName(code);
  });

  return (
    <div className={`websdk-phone-picker ${className}`}>
      <Label
        name={name}
        text={title}
        params={params}
        invalid={isInvalid}
        isRequired={isRequired}
      />
      <Suspense fallback={null}>
        {customCountrySelect ? (
          <>
            <Select
              name={`country-select-${name}`}
              labelId={`${name}-label`}
              options={customOptions}
              value={countryName}
              disabled={disableCountrySelect}
              onChange={onCustomCountryChange}
            />
            {countryName ? (
              <CountryIcon country={countryName} label={`${name}-icon`} />
            ) : null}
            <Input
              id={name}
              name={name}
              className='websdk-phone-picker-input'
              country={countryName}
              international
              withCountryCallingCode
              limitMaxLength
              countryCallingCodeEditable={false}
              value={phoneNumber}
              onChange={debouncedChangeHandler}
              onBlur={onBlurNumberInput}
            />
          </>
        ) : (
          <PhoneInput
            id={name}
            withCountryCallingCode
            disabled={disabled}
            defaultCountry={defaultCountry}
            countryCallingCodeEditable={false}
            countries={countryOptions}
            country={country}
            labels={labels}
            countryOptionsOrder={countriesOrder}
            value={phoneNumber}
            displayInitialValueAsLocalNumber={false}
            limitMaxLength
            international
            onChange={debouncedChangeHandler}
            onCountryChange={setCountryName}
            onBlur={onBlurNumberInput}
            countrySelectProps={{
              unicodeFlags: false,
              disabled: disableCountrySelect
            }}
          />
        )}
      </Suspense>
    </div>
  );
};

PhonePicker.propTypes = themePropTypes.phonePicker;

export default PhonePicker;
